import api from './api';
import {
  AuthUser,
  LoginCredentials,
  RegisterData,
  LoginResponse,
  LoginApiPayload,
  UserMeApiPayload,
  ChangePasswordData,
  PasswordResponse,
} from '../types';
import { isApiError } from '../types/api-error';

export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await api.post('/auth/login', credentials);

      if (response.status === 200 && response.data) {
        const backendData = response.data as LoginApiPayload;

        const authUser: AuthUser = {
          // Identidade canónica vem do GET /user/me. Mantém placeholder aqui.
          id: backendData.userId != null ? String(backendData.userId) : '',
          name: backendData.name ?? '',
          lastName: backendData.lastName ?? '',
          email: '',
          permissions: backendData.permissions ?? [],
          needsSubscription: backendData.needsSubscription === true,
          accessLevel: backendData.accessLevel,
          accessReason: backendData.accessReason,
          daysRemaining: backendData.daysRemaining,
          accessUntil: backendData.accessUntil != null ? String(backendData.accessUntil) : null,
          trialEndsAt: backendData.trialEndsAt != null ? String(backendData.trialEndsAt) : null,
          token: 'cookie-managed',
          // Tenant canónico vem do GET /user/me. Mantém placeholder aqui.
          establishmentId: backendData.establishmentId ?? '',
          establishmentWhatsAppEnabled: backendData.establishmentWhatsAppEnabled,
        };

        return {
          success: true,
          data: {
            user: authUser,
            token: 'cookie-managed',
          },
          message: 'Login realizado com sucesso',
        };
      }

      throw new Error('Resposta inesperada do servidor');
    } catch (error: unknown) {
      if (isApiError(error)) {
        throw error;
      }

      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : 'Erro ao fazer login';
      throw new Error(errorMessage);
    }
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error: unknown) {
      // Ignora erros de logout
    }
  },

  refreshToken: async (): Promise<string> => {
    try {
      await api.post('/auth/refresh');
      return 'cookie-managed';
    } catch (error: unknown) {
      throw new Error('Falha ao renovar autenticação');
    }
  },

  getCurrentUser: async (): Promise<LoginResponse> => {
    try {
      const response = await api.get('/user/me');

      const userData = response.data as UserMeApiPayload;

      const authUser: AuthUser = {
        id: String(userData.userId),
        name: userData.name,
        email: userData.email || '',
        permissions: Array.isArray(userData.permissions) ? userData.permissions : [],
        needsSubscription: userData.needsSubscription === true,
        accessLevel: userData.accessLevel,
        accessReason: userData.accessReason,
        daysRemaining: userData.daysRemaining,
        accessUntil: userData.accessUntil != null ? String(userData.accessUntil) : null,
        trialEndsAt: userData.trialEndsAt != null ? String(userData.trialEndsAt) : null,
        token: 'cookie-managed',
        establishmentId: String(userData.establishmentId),
        lastName: userData.lastName || '',
        nickname: userData.nickname || '',
        phone: userData.phone || '',
        cpf: userData.cpf || '',
        birthDate: userData.birthDate != null ? String(userData.birthDate) : '',
        photoUrl: (userData as { photoUrl?: string }).photoUrl ?? userData.photoPath ?? undefined,
        establishmentWhatsAppEnabled: userData.establishmentWhatsAppEnabled,
      };

      return {
        success: true,
        data: {
          user: authUser,
          token: 'cookie-managed',
        },
      };
    } catch (error: unknown) {
      if (
        isApiError(error) &&
        (error.response?.status === 400 ||
          error.response?.status === 401 ||
          error.response?.status === 404)
      ) {
        throw new Error('Não autenticado');
      }

      throw error;
    }
  },

  changePassword: async (data: ChangePasswordData): Promise<PasswordResponse> => {
    const response = await api.patch('/user/password', data);
    if (response.status === 204) {
      return { success: true, message: 'Senha alterada com sucesso' };
    }
    return response.data;
  },
};
